import { Injectable, Injector } from "@angular/core";
import { DataService } from "./data.service";
import { HttpWrapperService } from "./http.wrapper.service";
import { SessionManagerService } from "./SessionManager/session-manager.service";
import { Router } from "@angular/router";
import { observableToPromise } from "../admin/events-screen/utils/observableToPromise";
import { IndexDbService } from "../_Database/index-db-service";
import { IndexDBResult } from "../_Database/index-db-models";
import { mockUtils } from "../development/mocks/mock-utils";
import { validateEmail } from "../_helpers/validateEmail";
import { applicationUsersMock } from "../admin/users/mocks/app-users-mock";
import { Observable } from "rxjs/Observable";
import { BehaviorSubject } from "rxjs/BehaviorSubject";
import {
  AppUserFullModel,
  AppUserModel,
} from "../admin/users/user-management-models/app-user.model";
import { UserPrivilegeType } from "../admin/users/user-management-models/user-privilege-type.enum";
import { UserEditPermissionLevel } from "../admin/users/user-management-models/user-privilege-edit-type.enum";
import { UserNoalOrgan } from "../admin/users/user-management-models/user-noal-organ";
import { OrganType } from "../_models/organ-type";
import { Layer } from "../admin/events-screen/event-page-models/noal.models";
import { SaveActionResult } from "../admin/events-screen/event-page-models/save-action-result";
import { UserPostModel } from "../admin/users/user-management-models/user-post-model";
import { PersonalIdAdminValidator } from "../_directives";
import { GenericSearchState } from "../_models/GenericSearchState";
import { ServerNamespace } from "../_models/server/server-namespace";

interface AddHocUser {
  first_name: string;
  last_name: string;
  email: string;
  id: number;
  canEditEvent: boolean;
}

export type UsersTableSearchState = GenericSearchState;

@Injectable()
export class UserManagementService {
  public readonly usersTableSearchState$: BehaviorSubject<UsersTableSearchState> =
    new BehaviorSubject<UsersTableSearchState>(null);
  public readonly usersInTable$: BehaviorSubject<AppUserModel[]> =
    new BehaviorSubject([]);
  private indexDB: IndexDbService = IndexDbService.Instance;
  private MAX_NUMBER_OF_PARENT_EVENTS_ON_SCROLL: 200;
  constructor(
    dataService: DataService,
    private http: HttpWrapperService,
    private sessionManager: SessionManagerService,
    private injector: Injector,
    private router: Router
  ) {
    if (!this.http) {
      this.SaveResponseInIndexDb(new Response(), "heb");
    }
  }

  public static get devOnlyGetUSerMockList(): AppUserModel[] {
    return applicationUsersMock;
  }

  public static organTypeFromServerToEnum(organType: string): OrganType {
    switch (organType) {
      case "mahoz":
        return OrganType.County;
      case "ken":
        return OrganType.Nest;
      case "kehila":
        return OrganType.Circle;
      default:
        throw new Error("Organ type not found");
    }
  }

  public static organTypeFromEnumToServer(organType: OrganType): string {
    switch (organType) {
      case OrganType.County:
        return "mehozot";
      case OrganType.Nest:
        return "kenim";
      case OrganType.Circle:
        return "kehilot";
      default:
        throw new Error("Organ type not found");
    }
  }

  public static available_permissionsToUserOrgan(
    available_permissions: Array<{
      id: number;
      name: string;
      level: string;
      parent_organ: string;
    }>
  ): UserNoalOrgan[] {
    return available_permissions.map((permission) => {
      const organ: UserNoalOrgan = {
        id: +permission.id,
        name: permission.name,
        unique_identifier_code: `${permission.name}_${permission.level}`,
        parentName: permission.parent_organ,
        organType: UserManagementService.organTypeFromServerToEnum(
          permission.level
        ),
        status: "new",
      };
      return organ;
    });
  }

  public static validateUserName(userName: string): boolean {
    return userName && userName.length > 2 && /^[a-z0-9-]+$/.test(userName);
  }

  public static reverseCalculateUserSystemPrivileges(privileges: {
    userPrivilegeType: UserPrivilegeType;
    cashCollection: boolean;
    creditCollection: boolean;
    viewOnly: boolean;
  }) {
    return {
      is_superadmin:
        privileges.userPrivilegeType === UserPrivilegeType.SuperAdmin ? 1 : 0,
      is_talpan: privileges.creditCollection ? 1 : 0,
      is_komonar: privileges.cashCollection,
      is_mashatz: privileges.viewOnly,
    };
  }

  public static calculateUserSystemPrivilegesFromServer(serverUser: {
    is_superadmin: number;
    is_talpan: number;
    is_komonar: boolean;
    is_mashatz: boolean;
  }) {
    let privilegeType: UserPrivilegeType = UserPrivilegeType.Rakaz;
    if (serverUser.is_superadmin) {
      privilegeType = UserPrivilegeType.SuperAdmin;
    } else if (
      serverUser.is_talpan ||
      serverUser.is_komonar ||
      serverUser.is_mashatz
    ) {
      privilegeType = UserPrivilegeType.Limited;
    }
    return {
      userPrivilegeType: privilegeType,
      cashCollection: serverUser.is_komonar,
      creditCollection: serverUser.is_talpan === 1,
      viewOnly: serverUser.is_mashatz,
    };
  }

  public static getLayersFromGlobalNoalData(
    kehilot: Array<{
      id: number;
      name: string;
      has_add_permission: boolean;
      mehozot: Array<{
        id: number;
        name: string;
        has_add_permission: boolean;
        kenim: Array<{
          id: number;
          name: string;
          has_add_permission: boolean;
        }>;
      }>;
    }>,
    grades: Array<{
      id: number;
      name: string;
    }>,
    ken_collections: Array<{
      id: number;
      kenim: number[];
      name: string;
      is_kehila: boolean;
    }>
  ): Layer[] {
    const foundLayers = [];

    grades.forEach((grade) => {
      foundLayers.push({ layerId: grade.id, layerName: grade.name });
    });
    return foundLayers;
  }

  public static buildUUIDForOrgan(
    organ: {
      id: number;
      name: string;
      parent: string;
    },
    typeOfOrgan: OrganType
  ): string {
    return `${typeOfOrgan}_${organ.id}`;
  }

  public static serverOrgansToUserNoalOrgans(serverOrgans: {
    kenim: Array<{
      id: number;
      name: string;
      parent: string;
    }>;
    kehilot: Array<{
      id: number;
      name: string;
      parent: string;
    }>;
    mehozot: Array<{
      id: number;
      name: string;
      parent: string;
    }>;
  }): UserNoalOrgan[] {
    const mehozot: UserNoalOrgan[] = (serverOrgans.mehozot || []).map((o) => ({
      id: o.id,
      name: o.name,
      organType: OrganType.County,
      unique_identifier_code: UserManagementService.buildUUIDForOrgan(
        o,
        OrganType.County
      ),
      parentName: o.parent,
      status: "new",
    })) as UserNoalOrgan[];
    const kenim: UserNoalOrgan[] = (serverOrgans.kenim || []).map((o) => ({
      id: o.id,
      organType: OrganType.Nest,
      name: o.name,
      unique_identifier_code: UserManagementService.buildUUIDForOrgan(
        o,
        OrganType.Nest
      ),
      parentName: o.parent,
      status: "new",
    })) as UserNoalOrgan[];
    const kehilot = (serverOrgans.kehilot || []).map((o) => ({
      id: o.id,
      organType: OrganType.Circle,
      name: o.name,
      unique_identifier_code: UserManagementService.buildUUIDForOrgan(
        o,
        OrganType.Circle
      ),
      parentName: o.parent,
      status: "new",
    })) as UserNoalOrgan[];

    return [...kenim, ...mehozot, ...kehilot];
  }

  public static userSelectedOrgansToPermissions(
    userSelectedOrgans: UserNoalOrgan[]
  ) {
    const permissions: any = {};
    const typeOfOrgan = userSelectedOrgans.map((o) => o.organType)[0];
    if (!typeOfOrgan) {
      return permissions;
    }

    let propertyName =
      UserManagementService.organTypeFromEnumToServer(typeOfOrgan);

    const organsToAdd = userSelectedOrgans.filter(
      (o) => o.status === "exiting"
    );
    const organsToRemove = userSelectedOrgans.filter(
      (o) => o.status === "deleted"
    );
    if (organsToRemove.length) {
      permissions.remove = {};
      permissions.remove[propertyName] = organsToRemove.map((o) => o.id);
    }
    if (organsToAdd.length) {
      permissions.add = {};
      permissions.add[propertyName] = organsToAdd.map((o) => o.id);
    }
    return permissions;
  }

  public static serializeUserActivityFieldsDataToPostData(
    userDataFromForm: any
  ): Partial<UserPostModel> {
    const userSelectedOrgans =
      userDataFromForm.activityFields.userSelectedOrgans || [];
    const postData = {
      permissions:
        UserManagementService.userSelectedOrgansToPermissions(
          userSelectedOrgans
        ),
    };
    return postData;
  }

  public static serializeUserDataToPostData(
    userDataFromForm: any
  ): UserPostModel {
    const reverseCalculateUserSystemPrivileges1 =
      UserManagementService.reverseCalculateUserSystemPrivileges({
        cashCollection: userDataFromForm.systemPrivileges.cashCollection,
        creditCollection: userDataFromForm.systemPrivileges.creditCollection,
        userPrivilegeType: userDataFromForm.systemPrivileges
          .privilegeType as UserPrivilegeType,
        viewOnly: userDataFromForm.systemPrivileges.viewOnly,
      });

    const userSelectedOrgans =
      userDataFromForm.activityFields.userSelectedOrgans || [];

    const postData = {
      username: userDataFromForm.personalDetails.userName,
      first_name: userDataFromForm.personalDetails.firstName,
      last_name: userDataFromForm.personalDetails.lastName,
      cellular: userDataFromForm.personalDetails.phoneNumber,
      email: userDataFromForm.personalDetails.email,
      taz: userDataFromForm.personalDetails.israeliId,
      is_superadmin: reverseCalculateUserSystemPrivileges1.is_superadmin
        ? 1
        : 0,
      is_talpan: reverseCalculateUserSystemPrivileges1.is_talpan ? 1 : 0,
      is_komonar: reverseCalculateUserSystemPrivileges1.is_komonar ? 1 : 0,
      is_mashatz: reverseCalculateUserSystemPrivileges1.is_mashatz ? 1 : 0,
      layers: "", // refund_code: userDataFromForm.systemPrivileges.creditCollection ,
      is_active: userDataFromForm.systemPrivileges.isActive,
      is_deleted: userDataFromForm.systemPrivileges.isDeleted, // last_logged_in: "2024-09-22",
      permissions:
        UserManagementService.userSelectedOrgansToPermissions(
          userSelectedOrgans
        ),
    };

    if (
      (userDataFromForm.systemPrivileges &&
        userDataFromForm.systemPrivileges.privilegeType ===
          UserPrivilegeType.Rakaz) ||
      userDataFromForm.systemPrivileges.privilegeType ===
        UserPrivilegeType.SuperAdmin
    ) {
      postData.is_talpan = 0;
      postData.is_komonar = 0;
      postData.is_mashatz = 0;
    }
    return postData;
  }

  public static serializeFormInitEditUser(userFormInitFromServer: {
    available_permissions: Array<{
      id: number;
      name: string;
      level: string;
      parent_organ: string;
    }>;
    user_details: {
      id: number;
      username: string;
      first_name: string;
      last_name: string;
      cellular: string;
      email: string;
      taz: string;
      is_superadmin: number;
      is_talpan: number;
      is_komonar: boolean;
      is_mashatz: boolean;
      layers: any[];
      is_meshutaf: number;
      refund_code: string;
      is_active: boolean;
      is_deleted: boolean;
      last_logged_in: string | null;
      permissions: {
        has_extra_unseen_permission: boolean;
        kenim: Array<{
          id: number;
          name: string;
          parent: string;
        }>;
        kehilot: Array<{
          id: number;
          name: string;
          parent: string;
        }>;
        mehozot: Array<{
          id: number;
          name: string;
          parent: string;
        }>;
      };
      edit_level: "none" | "view" | "edit_permissions" | "edit_details";
    };
  }): { fullUserModel: AppUserFullModel; additionalData: any } {
    const { userPrivilegeType, cashCollection, creditCollection, viewOnly } =
      UserManagementService.calculateUserSystemPrivilegesFromServer(
        userFormInitFromServer.user_details
      );
    return {
      fullUserModel: {
        id: userFormInitFromServer.user_details.id.toString(),
        firstName: userFormInitFromServer.user_details.first_name,
        lastName: userFormInitFromServer.user_details.last_name,
        userName: userFormInitFromServer.user_details.username,
        email: userFormInitFromServer.user_details.email,
        mobileNumber: userFormInitFromServer.user_details.cellular,
        activityCircles: "",
        isActive: userFormInitFromServer.user_details.is_active,
        lastLogin: userFormInitFromServer.user_details.last_logged_in
          ? new Date(userFormInitFromServer.user_details.last_logged_in)
          : null,
        canEditPermissionLevel: userFormInitFromServer.user_details
          .edit_level as UserEditPermissionLevel,
        israeliId: userFormInitFromServer.user_details.taz,
        cashCollection,
        creditCollection,
        viewOnly,
        userPrivilegeType,
        currentOrgans: UserManagementService.serverOrgansToUserNoalOrgans(
          userFormInitFromServer.user_details.permissions
        ).map((o) => ({
          ...o,
          status: "exiting",
        })) as UserNoalOrgan[],
        has_extra_unseen_permission:
          userFormInitFromServer.user_details.permissions
            .has_extra_unseen_permission,

        availableOrgans: UserManagementService.available_permissionsToUserOrgan(
          userFormInitFromServer.available_permissions
        ).map((o) => ({
          ...o,
          status: "new",
        })) as UserNoalOrgan[],
      },
      additionalData: {},
    };
  }

  public static isNewUser(userId: string | "new"): boolean {
    return userId === "new";
  }

  public static serverUserToAppUser(serverUser: any): AppUserModel {
    return {
      id: serverUser.id.toString(),
      firstName: serverUser.first_name,
      lastName: serverUser.last_name,
      userName: serverUser.username,
      email: serverUser.email,
      mobileNumber: serverUser.cellular,
      activityCircles: serverUser.permissions,
      isActive: serverUser.is_active,
      lastLogin: UserManagementService.serverLastLoginToDate(
        serverUser.last_logged_in
      ),
      canEditPermissionLevel: serverUser.edit_level as UserEditPermissionLevel,
    };
  }

  public static DevMockAddHocUserToStandardUSer(
    addHoc: AddHocUser
  ): AppUserModel {
    const mobileNumber = `05${mockUtils.createIntRandomIntInRange(
      0,
      5
    )}-${mockUtils.createIntRandomIntInRange(1234567, 9876543).toString()}`;
    const activityCircles = mockUtils.citiesAndNests
      .filter((city, i) => {
        return i < 5;
      })
      .join(", ");
    const randomDaysBefore = mockUtils.createIntRandomIntInRange(0, 20);
    return {
      canEditPermissionLevel: addHoc.canEditEvent
        ? UserEditPermissionLevel.EditPermissions
        : UserEditPermissionLevel.EditDetails,
      id: addHoc.id.toString(),
      firstName: addHoc.first_name,
      lastName: addHoc.last_name,
      userName: addHoc.email.split("@")[0],
      email: `${addHoc.email}@noal.org.il`,
      mobileNumber,
      activityCircles,
      isActive: Math.random() > 0.3,
      lastLogin: new Date(
        new Date().getTime() - 1000 * 60 * 60 * 24 * randomDaysBefore
      ),
    };
  }

  public static validatePhoneNumber(fieldValue: string) {
    const PHONE_REGEXP = /^05\d([-]?)\d{7}$/;
    return PHONE_REGEXP.test(fieldValue);
  }

  public static validateIsraeliId(id: string): boolean {
    return PersonalIdAdminValidator.isValidIsraeliID(id);
  }

  public static validateEmail(email: string): boolean {
    return validateEmail(email);
  }

  public static serverLastLoginToDate(lastLogin: string): Date | null {
    return lastLogin ? new Date(lastLogin) : null;
  }

  public getAllUsersInTable(): Observable<AppUserModel[]> {
    return this.usersInTable$.asObservable();
  }

  public async searchUserFromAutoComplete(
    search: string
  ): Promise<AppUserModel[]> {
    return this.searchUser({ search, searchType: "autoComplete" });
  }

  public async searchUser({
    search,
    searchType,
    from_date,
    to_date,
    is_active,
    kenim,
    mehozot,
    kehilot,
  }: {
    search: string;
    searchType: "autoComplete" | "standard" | "advanced";
    from_date?: string | null;
    to_date?: string | null;
    is_active?: boolean | null;
    kenim?: number[] | null;
    mehozot?: number[] | null;
    kehilot?: number[] | null;
  }): Promise<AppUserModel[]> {
    const userSearchParams = {
      from_date,
      to_date,
      is_active,
      kenim,
      mehozot,
      kehilot,
      search,
    };
    const activeParams = {};
    for (const key in userSearchParams) {
      if (userSearchParams[key]) {
        activeParams[key] = userSearchParams[key];
      }
    }
    if (is_active === false) {
      activeParams["is_active"] = false;
    } else if (searchType === "standard") {
      //activeParams["is_active"] = true;
    }
    const response = await observableToPromise(
      this.http.get(`/django-api/harshama-users/`, { params: activeParams })
    );
    const resultsData = await response.json();
    const users = (resultsData.results || []).map((user) =>
      UserManagementService.serverUserToAppUser(user)
    );
    if (searchType !== "autoComplete") {
      this.usersInTable$.next(users);
      this.usersTableSearchState$.next({
        next: resultsData.next,
        count: resultsData.count,
        previous: resultsData.previous,
        isLoading: false,
      });
    }
    return users;
  }

  public async GetUserDataAndEditorDataForEdit(
    userId: string
  ): Promise<Partial<AppUserFullModel>> {
    const response = await observableToPromise(
      this.http.get(
        `/django-api/harshama-users/${userId}/update_form_initial_data/`
      )
    );
    const userDataForFormInit = await response.json();

    const { fullUserModel } =
      UserManagementService.serializeFormInitEditUser(userDataForFormInit);
    return fullUserModel;
  }

  public async checkForFieldAvailability(
    params: {
      search: string;
    },
    fieldType: "userName" | "phoneNumber" | "email" | "israeliId"
  ): Promise<"available" | "notAvailable"> {
    let fieldName = "username";
    switch (fieldType) {
      case "userName":
        fieldName = "username";
        break;
      case "phoneNumber":
        fieldName = "cellular";
        break;
      case "email":
        fieldName = "email";
        break;
      case "israeliId":
        fieldName = "taz";
        break;
    }
    const parameters = { [fieldName]: params.search };
    // @ts-ignore
    const results = await this.searchExistingUsers(parameters);

    return results && results.exists ? "notAvailable" : "available";
  }

  public async getCreateUserFormInitialData(): Promise<any> {
    const response = await observableToPromise(
      this.http.get("/django-api/harshama-users/create_form_initial_data/")
    );
    const userFormNoalData = await response.json();
    return {
      availableOrgans: UserManagementService.available_permissionsToUserOrgan(
        userFormNoalData.available_permissions
      ).map((o) => ({
        ...o,
        status: "new",
      })) as UserNoalOrgan[],
    };
  }

  public tryToUpdateUsersStatusInSearch(
    userIds: string[],
    status: "active" | "inactive"
  ) {
    const users = this.usersInTable$.getValue().map((u) => ({ ...u }));
    users.forEach((user) => {
      if (userIds.includes(user.id)) {
        user.isActive = status === "active";
      }
    });
    this.usersInTable$.next(users);
  }

  public async markUsersAsActiveOrInactive(
    userIds: string[],
    status: "active" | "inactive"
  ): Promise<SaveActionResult> {
    const action = status === "active" ? "activate" : "deactivate";
    try {
      const response = await observableToPromise(
        this.http.post(`/django-api/harshama-users/${action}/`, {
          users: userIds,
        })
      );
      const body = await response.json();
      if (body) {
        return { success: true, data: body };
      }
      return { success: false, error: body };
    } catch (err) {
      return { success: false, error: err };
    }
  }

  public async searchExistingUsers(
    params:
      | {
          taz: string;
        }
      | { email: string }
      | { cellular: string }
      | { username: string }
  ): Promise<{ exists: boolean }> {
    try {
      const response = await observableToPromise(
        this.http.get("/django-api/harshama-users/exist/", {
          params,
        })
      );

      const body = await response.json();

      if (!body) {
        return null;
      }
      return body;
    } catch (err) {
      return null;
    }
  }

  public async saveExistingUser(
    userPostModel: UserPostModel,
    userId: string
  ): Promise<SaveActionResult> {
    try {
      let response;

      if (userId === "new") {
        response = await observableToPromise(
          this.http.post(`/django-api/harshama-users/`, userPostModel)
        );
      } else {
        response = await observableToPromise(
          this.http.patch(
            `/django-api/harshama-users/${userId}/`,
            userPostModel
          )
        );
      }

      const body = await response.json();
      if (body) {
        return { success: true, data: body };
      }
      return { success: false, error: body };
    } catch (err) {
      return { success: false, error: err };
    }
  }

  public async saveNewUser(
    userPostModel: UserPostModel,
    userId: string
  ): Promise<SaveActionResult> {
    try {
      let response;
      const newUserPostModel = JSON.parse(
        JSON.stringify(userPostModel)
      ) as UserPostModel;
      newUserPostModel.permissions = {
        ...userPostModel.permissions.add,
      };

      response = await observableToPromise(
        this.http.post(`/django-api/harshama-users/`, newUserPostModel)
      );

      const body = await response.json();
      if (body) {
        return { success: true, data: body };
      }
      return { success: false, error: body };
    } catch (err) {
      return { success: false, error: err };
    }
  }
  public async scrolledToBottomOfResults() {
    const searchState = this.usersTableSearchState$.getValue();
    if (!searchState) {
      return;
    }
    const allUsersInTable = this.usersInTable$.getValue();
    if (
      searchState.isLoading ||
      !searchState.next ||
      allUsersInTable.length > this.MAX_NUMBER_OF_PARENT_EVENTS_ON_SCROLL
    ) {
      return;
    }
    try {
      this.usersTableSearchState$.next({ ...searchState, isLoading: true });

      const addressParams = searchState.next.split(`harshama-users/?`)[1];
      const response = await observableToPromise(
        this.http.get(`/django-api/harshama-users/?${addressParams}`)
      );

      const resultsData = (await response.json()) as any;
      const serializedUsers = (resultsData.results || []).map((user) =>
        UserManagementService.serverUserToAppUser(user)
      );

      const existingUsers = allUsersInTable.map((parentEvent) => ({
        ...parentEvent,
      }));

      const allExistingUserIds = existingUsers.map((pe) => pe.id);
      const newUsersWithoutDuplicated = serializedUsers.filter(
        (pe) => !allExistingUserIds.includes(pe.discountId)
      );

      existingUsers.push(...newUsersWithoutDuplicated);

      this.usersInTable$.next(existingUsers);
      this.usersTableSearchState$.next({
        next: resultsData.next,
        count: resultsData.count,
        previous: resultsData.previous,
        isLoading: false,
      });
    } catch (err) {}
  }
  private SaveResponseInIndexDb(res: Response, lang: string): void {
    this.indexDB
      .ready(1000)
      .then((ready) => {
        if (ready) {
          return this.indexDB.put("Cache", {
            id: "1",
            [lang || 1]: res,
          });
        } else {
          return Promise.reject("IndexDB not ready");
        }
      })
      .then((indexDBResult: IndexDBResult<any>) => {
        if (indexDBResult.data.result) {
          return indexDBResult;
        } else {
          return false;
        }
      })
      .catch((e) => e);
  }
}



// WEBPACK FOOTER //
// ./src/app/_services/user-management.service.ts